/** Calendar coordinates are absolute days, so adding earlier tasks never moves the viewport. */
export const DAY_WIDTH = 96;
export const ROW_HEIGHT = 256;
export const CARD_HEIGHT = 128;
export const MIN_ZOOM = 0.08;
export const MAX_ZOOM = 3;
export const DETAIL_ZOOM = 0.6;
const DAY_MS = 86_400_000;

export type Camera = { day: number; y: number; zoom: number };
export type Size = { width: number; height: number };
export type Point = { x: number; y: number };
export type ScheduleTask = {
  id: string; start_date?: string | null; target_date?: string | null;
  deleted_at?: string | null; archived_at?: string | null;
};
export type Dependency = { id: string; predecessor_id: string; successor_id: string };
export type ScheduleNode<T extends ScheduleTask = ScheduleTask> = { task: T; start: number; end: number; y: number };
export type Bounds = { start: number; end: number; top: number; bottom: number };

export function dateDay(value: string | null | undefined): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const timestamp = Date.parse(value + "T00:00:00Z");
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value ? timestamp / DAY_MS : null;
}

export function dayDate(day: number): Date { return new Date(Math.floor(day) * DAY_MS); }

export function taskSchedule(task: ScheduleTask): { start: number; end: number } | null {
  if (task.deleted_at || task.archived_at) return null;
  const start = dateDay(task.start_date), end = dateDay(task.target_date);
  if (start === null && end === null) return null;
  // A single registered date occupies that day. End dates are inclusive.
  return { start: Math.min(start ?? end!, end ?? start!), end: Math.max(start ?? end!, end ?? start!) + 1 };
}

export function layoutSchedule<T extends ScheduleTask>(tasks: T[], dependencies: Dependency[]) {
  const dated = tasks.flatMap((task) => {
    const dates = taskSchedule(task);
    return dates ? [{ task, ...dates }] : [];
  }).sort((a, b) => a.start - b.start || b.end - a.end || a.task.id.localeCompare(b.task.id));
  const rowEnds: number[] = [];
  const nodes: ScheduleNode<T>[] = dated.map((item) => {
    let row = rowEnds.findIndex((end) => end + 0.65 <= item.start);
    if (row === -1) row = rowEnds.length;
    rowEnds[row] = item.end;
    return { ...item, y: 64 + row * ROW_HEIGHT };
  });
  const ids = new Set(nodes.map((n) => n.task.id));
  // Never invent a dependency to bridge a hidden, undated or filtered task.
  const edges = dependencies.filter((e) => ids.has(e.predecessor_id) && ids.has(e.successor_id) && e.predecessor_id !== e.successor_id);
  const bounds: Bounds | null = nodes.length ? {
    start: nodes[0]!.start, end: Math.max(...nodes.map((n) => n.end)),
    top: 0, bottom: 64 + (rowEnds.length - 1) * ROW_HEIGHT + CARD_HEIGHT + 48,
  } : null;
  return { nodes, edges, bounds };
}

export const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

export function fitCamera(bounds: Bounds, size: Size): Camera {
  const zoom = clampZoom(Math.min(1, Math.max(1, size.width - 120) / ((bounds.end - bounds.start) * DAY_WIDTH), Math.max(1, size.height - 100) / (bounds.bottom - bounds.top)));
  return { day: (bounds.start + bounds.end) / 2, y: (bounds.top + bounds.bottom) / 2, zoom };
}

export function zoomAt(camera: Camera, zoom: number, point: Point, size: Size): Camera {
  const next = clampZoom(zoom);
  return {
    zoom: next,
    day: camera.day + (point.x - size.width / 2) / DAY_WIDTH * (1 / camera.zoom - 1 / next),
    y: camera.y + (point.y - size.height / 2) * (1 / camera.zoom - 1 / next),
  };
}

export function panCamera(camera: Camera, delta: Point): Camera {
  return { ...camera, day: camera.day - delta.x / (DAY_WIDTH * camera.zoom), y: camera.y - delta.y / camera.zoom };
}

export function nodeRect(node: ScheduleNode, camera: Camera, size: Size) {
  const width = (node.end - node.start) * DAY_WIDTH * camera.zoom;
  // Short date ranges keep their true width; more detail appears when they are wide enough.
  const detailed = camera.zoom >= DETAIL_ZOOM && width >= 210;
  const height = detailed ? CARD_HEIGHT : Math.min(34, ROW_HEIGHT * camera.zoom - 6);
  const centerY = (node.y + CARD_HEIGHT / 2 - camera.y) * camera.zoom + size.height / 2;
  return { x: (node.start - camera.day) * DAY_WIDTH * camera.zoom + size.width / 2, y: centerY - height / 2, width, height, detailed };
}

export function relatedTasks(id: string | null, edges: Dependency[]): Set<string> {
  if (!id) return new Set();
  const result = new Set([id]);
  for (const upstream of [true, false]) {
    const visited = new Set<string>(), pending = [id];
    while (pending.length) {
      const current = pending.pop()!;
      if (visited.has(current)) continue;
      visited.add(current); result.add(current);
      for (const edge of edges) {
        if ((upstream ? edge.successor_id : edge.predecessor_id) === current) pending.push(upstream ? edge.predecessor_id : edge.successor_id);
      }
    }
  }
  return result;
}

export function validCamera(value: unknown): value is Camera {
  if (!value || typeof value !== "object") return false;
  const v = value as Camera;
  return Number.isFinite(v.day) && Math.abs(v.day) < 1_000_000 && Number.isFinite(v.y) && Math.abs(v.y) < 10_000_000 && Number.isFinite(v.zoom) && v.zoom >= MIN_ZOOM && v.zoom <= MAX_ZOOM;
}
